import React from "react";
import PropTypes from "prop-types";
import Link from "next/link";
import clsx from "clsx";

import {LoadingState} from "@/components/LoadingState";

const Button = ({
  label, // Button text
  onClick = null, // Click handler
  type = "button", // Button type (default: 'button')
  isLoading = false, // Show loading spinner if true
  disabled = false, // Disable button if true
  fullWidth = false, // Make button full-width if true
  className, // Custom class name
  iconsize = "text-xl",
  variant,
  size,
  icon,
  iconPosition,
  href,
  ...props
}) => {
  // Determine the size class
  const sizeClass = (z) => {
    switch (z) {
      case "2xl":
        return "min-w-16 min-h-16";
      case "xl":
        return "min-w-14 min-h-14";
      case "2lg":
        return "min-w-10 sm:min-w-12 min-h-10 sm:min-h-12";
      case "lg":
        return "min-w-8 sm:min-w-10 min-h-8 sm:min-h-10";
      case "md":
        return "min-w-8 min-h-8";
      case "sm":
        return "min-w-6 min-h-6";
      default:
        return "min-w-10 sm:min-w-12 min-h-10 sm:min-h-12"; // Default size
    }
  };
  const buttonVariant = (v) => {
    switch (v) {
      case "solid":
        return "bg-[rgba(var(--primary-rgb)/100%)] text-white";
      case "outline":
        return "border border-[rgba(var(--primary-rgb)/100%)] text-[rgba(var(--primary-rgb)/100%)]";
      case "transparent":
        return "bg-[rgba(var(--primary-rgb)/15%)] text-[rgba(var(--primary-rgb)/100%)]";
      case "danger":
        return "bg-red-600 text-white hover:bg-red-700";
      case "danger-outline":
        return "border border-red-600 text-red-600 hover:bg-red-50";
      default:
        return "bg-transparent text-[rgba(var(--primary-rgb)/100%)] hover:bg-[rgba(var(--primary-rgb)/10%)]"; // Default size
    }
  };
  return (
    <ButtonType
      href={href}
      type={type}
      onClick={onClick}
      className={clsx(
        `inline-flex items-center justify-center text-sm font-medium rounded-lg `, // Base styles and additional className
        { "w-full": fullWidth },
        label ? "px-3 sm:px-4" : "text-xl",
        sizeClass(size),
        buttonVariant(variant),
        `${className}`
      )}
      disabled={disabled || isLoading}
      {...props}
    >
      {icon && iconPosition !== "right" && (
        <span
          className={clsx(
            "inline-block text-xl ",
            iconsize ? iconsize : "text-xl",
            label ? "mr-2" : "" 
          )}
        >
          {icon}
        </span>
      )}
      {label && label}
      { icon && iconPosition === "right" && (
        <span
          className={clsx(
            "inline-block text-xl ",
            iconsize ? iconsize : "text-xl",
            label ? "ml-2" : ""
          )}
        >
          {icon}
        </span>
      )}
      {isLoading ? (
        <LoadingState/>
      ) : null}
    </ButtonType>
  );
};

const ButtonType = ({href, children, ...props}) => (
  href ? 
    <Link href={href} {...props}>{children}</Link> 
    : 
    <button {...props}>{children}</button>
);

Button.propTypes = {
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  type: PropTypes.string,
  isLoading: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  fullWidth: PropTypes.bool,
  iconsize: PropTypes.string,
  variant: PropTypes.string,
  size: PropTypes.string,
  icon: PropTypes.node,
  iconPosition: PropTypes.string,
  href: PropTypes.string,
};
ButtonType.propTypes = {
  href: PropTypes.string,
  children: PropTypes.node.isRequired,
  props: PropTypes.node,
};

export default Button;
